import { getTimeZones } from '@vvo/tzdb';

import { useCurrentUser } from '@/hooks';
import { type User } from '@/types';
import { SeatType } from '@/types';
import {
  Button,
  Card,
  CardContent,
  Chip,
  Icon,
  IconButton,
  Typography,
} from '@/ui';
import { capitalize, formatMonthDayYear, toExternalUrl } from '@/utils';

import type { FC } from 'react';

import { useAppDispatch } from '@/store/hooks';
import { setChatId } from '@/store/slices/appSlice';
import { isAdmin } from '@/types/user';
import { formatContractEndLabel } from '@/utils/contractEnd';

interface InformationProps {
  client: User;
  hideActions?: boolean;
  onProfile?: () => void;
}

const Information: FC<InformationProps> = ({
  client,
  hideActions = false,
  onProfile,
}) => {
  const dispatch = useAppDispatch();
  const { isUserLoading, user } = useCurrentUser();
  const isAdminUser = !isUserLoading && isAdmin(user);

  const linkedInUrl = toExternalUrl(client.profile?.linkedIn);

  const handleMessageClick = () => {
    dispatch(setChatId(client.cometChatId));
  };

  const handleLinkedInClick = () => {
    window.open(linkedInUrl, '_blank');
  };

  const timezone = client.profile?.timezone;

  let timeZone = 'Timezone not set';
  if (timezone) {
    const t = getTimeZones().find(
      (tz) => timezone === tz.name || tz.group.includes(timezone || ''),
    );

    if (t) {
      const [time] = t.currentTimeFormat.split(' ');
      timeZone = `(GMT${time}) ${t.mainCities[0]}`;
    }
  }

  // TODO: Clean this up when we either get rid of the teams legacy multi growth plan
  // or truly handle multiple growth plans.
  const hasStartedGrowthPlan = client.growthPlans[0]?.started || false;

  const insightStatus = capitalize(
    client.insightsStatus?.replace('_', ' ') || '',
  );
  const contractEndLabel = formatContractEndLabel(client.contractEnd);

  const getSeatTypeDisplay = (seatType?: SeatType) => {
    if (!seatType) return 'Not Set';
    return seatType === SeatType.standard_leadership
      ? 'Standard/Leadership'
      : 'Executive';
  };

  return (
    <Card>
      <CardContent className="flex w-full flex-col">
        <Typography className="mb-2 flex justify-between" variant="subtitle1">
          Member Details{' '}
          {!client.isActive && <Chip color="error" label="Member Inactive" />}
        </Typography>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <Icon className="text-sage-500" fontSize="small">
              account_circle
            </Icon>
            <Typography
              className="break-after-all text-cement-400"
              variant="body2"
            >
              {client.email}
            </Typography>
          </div>
          {isAdminUser && (
            <div className="flex items-center gap-2">
              <Icon className="text-sage-500" fontSize="small">
                note
              </Icon>
              <Typography
                className="break-after-all text-cement-400"
                variant="body2"
              >
                Documents email: {client.documentsEmail ?? 'No email'}
              </Typography>
            </div>
          )}
          {client.profile?.createdAt && (
            <div className="flex items-center gap-2">
              <Icon className="text-sage-500" fontSize="small">
                device_reset
              </Icon>
              <Typography
                className="break-after-all text-cement-400"
                variant="body2"
              >
                Joined {formatMonthDayYear(client.profile.createdAt)}
              </Typography>
            </div>
          )}
          {client.lastLogin && (
            <div className="flex items-center gap-2">
              <Icon className="text-sage-500" fontSize="small">
                record_voice_over
              </Icon>
              <Typography
                className="break-after-all text-cement-400"
                variant="body2"
              >
                Last online {formatMonthDayYear(client.lastLogin)}
              </Typography>
            </div>
          )}
          {client.profile?.location && (
            <div className="flex items-center gap-2">
              <Icon className="text-sage-500" fontSize="small">
                my_location
              </Icon>
              <Typography
                className="break-after-all text-cement-400"
                variant="caption"
              >
                {client.profile.location}
              </Typography>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Icon className="text-sage-500" fontSize="small">
              schedule
            </Icon>
            <Typography
              className="break-after-all text-cement-400"
              variant="body2"
            >
              {timeZone}
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <Icon className="text-sage-500" fontSize="small">
              insights
            </Icon>
            <Typography
              className="break-after-all text-cement-400"
              variant="body2"
            >
              Insights Status: {insightStatus}
            </Typography>
          </div>
          {contractEndLabel && (
            <div className="flex items-center gap-2">
              <Icon className="text-sage-500" fontSize="small">
                event
              </Icon>
              <Typography
                className="break-after-all text-cement-400"
                variant="body2"
              >
                {contractEndLabel}
              </Typography>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Icon className="text-sage-500" fontSize="small">
              landscape
            </Icon>
            <Typography
              className="break-after-all text-cement-400"
              variant="body2"
            >
              Growth Plan: {hasStartedGrowthPlan ? 'Started' : 'Not Started'}
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <Icon className="text-sage-500" fontSize="small">
              account_box
            </Icon>
            <Typography
              className="break-after-all text-cement-400"
              variant="body2"
            >
              Seat Type: {getSeatTypeDisplay(client.profile?.seatType)}
            </Typography>
          </div>
          {isAdminUser && (
            <>
              <div className="flex items-center gap-2">
                <Icon className="text-sage-500" fontSize="small">
                  assignment
                </Icon>
                <Typography
                  className="break-after-all text-cement-400"
                  variant="body2"
                >
                  Matching preference:&nbsp;
                  {client.profile?.matchPreference ?? 'Not Selected'}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <Icon className="text-sage-500" fontSize="small">
                  article
                </Icon>
                <Typography
                  className="break-after-all text-cement-400"
                  variant="body2"
                >
                  Documents folder:&nbsp;
                  {client.folderId ? (
                    <a
                      href={`https://drive.google.com/drive/u/0/folders/${client.folderId}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Folder Link
                    </a>
                  ) : (
                    'Not folder id'
                  )}
                </Typography>
              </div>
            </>
          )}
        </div>
        {!hideActions && (
          <div className="mt-3 flex w-full gap-3">
            {linkedInUrl && (
              <IconButton variant="outlined" onClick={handleLinkedInClick}>
                <Icon>linkedin</Icon>
              </IconButton>
            )}
            <IconButton variant="outlined" onClick={onProfile}>
              <Icon>person</Icon>
            </IconButton>
            <Button
              className="flex-1"
              variant="contained"
              onClick={handleMessageClick}
            >
              Message
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default Information;
